import React, { useEffect, useState, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { Buffer } from "buffer";
import { Snackbar, Alert } from "@mui/material";
import {
  ArrowLeftIcon,
  ClockIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  PaperClipIcon,
  PaperAirplaneIcon,
  XMarkIcon,
  ChatBubbleLeftEllipsisIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";

import ImagePreview from "../../../../components/ImagePreview";
import { TicketStatusStepper } from "./";
import { serviceRequestApi } from "../../../../api/serviceRequest";
import { amendmentApi } from "../../../../api/amendment";
import LoadingOverlay from "../../../../components/LoadingOverlay";
import { RepairAndInquiriesStatusEntity } from "../../../../customEntites/RepairAndInquiries.entity";
import { GetServiceRequestEntity, ContractEntity } from "../../../../entities";
import { AttachmentEntity } from "../../../../customEntites/Attachment.entity";
import { GlobalContext } from "../../../../App";
import { ContextEntity } from "../../../../customEntites/context.entity";
import { generateError } from "../../../../services/error.service";

// `user_comment` entries are authored by the tenant (current app user).
// The "CreatedBy:..." suffix carries an internal user id which is not meant for display.
const parseCommentText = (raw?: string): string => {
  if (!raw) return '';
  return raw.split(/CreatedBy\s*:\s*/i)[0].replace(/\n+$/, '').trim();
};

const STATUS_STYLES: Record<string, { bg: string; text: string; Icon: React.ElementType }> = {
  'open':        { bg: 'bg-orange-50',  text: 'text-orange-500', Icon: ClockIcon },
  'in-progress': { bg: 'bg-[#e8f4fc]',  text: 'text-[#218cda]',  Icon: ArrowPathIcon },
  'completed':   { bg: 'bg-green-50',   text: 'text-green-600',  Icon: CheckCircleIcon },
}

// Backend stores description as an HTML block with SAP metadata. Pull out only
// the actual user input (after "Beschreibung:" / "Description:" label).
const extractUserDescription = (raw?: string): string => {
  if (!raw) return ''
  const match = raw.match(/<b>\s*(?:Beschreibung|Description)\s*:\s*<\/b>\s*([\s\S]*?)(?:<br\s*\/?>\s*<br|<b>|$)/i)
  if (match) return match[1].replace(/<br\s*\/?>/gi, ' ').trim()
  return raw.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
}

// Extract a single labelled field like `<b>Kategorie:</b> Sanitär<br>`.
const extractField = (raw: string | undefined, label: string): string | null => {
  if (!raw) return null
  const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const re = new RegExp(`<b>\\s*${escaped}\\s*:\\s*<\\/b>\\s*([\\s\\S]*?)(?:<br|<b>|$)`, 'i')
  const match = raw.match(re)
  const value = match ? match[1].trim() : null
  return value && value.length > 0 ? value : null
}

// Backend stores values in German. Map to translation keys so the value follows
// the user's selected language.
const VALUE_TRANSLATION_KEYS: Record<string, string> = {
  'Sanitär': 'PLUMBING',
  'Elektrik': 'ELECTRICAL',
  'Heizung': 'HEATING',
  'Gerät': 'APPLIANCE',
  'Sonstiges': 'OTHER',
  'Notfall': 'EMERGENCY',
  'Hoch': 'HIGH',
  'Mittel': 'MEDIUM',
  'Niedrig': 'LOW',
  'Ja': 'YES',
  'Nein': 'NO',
}

export const RequestContent = ({
  activeItem,
  statuses,
}: {
  activeItem: GetServiceRequestEntity;
  statuses: RepairAndInquiriesStatusEntity[];
}) => {
  const [attachments, setAttachments] = useState<AttachmentEntity[]>([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(true);
  const [serviceRequest, setServiceRequest] = useState<GetServiceRequestEntity | null>(null);
  const [messageText, setMessageText] = useState('');
  const [messageFiles, setMessageFiles] = useState<File[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [showCommentForm, setShowCommentForm] = useState(false);
  // Files that were just attached via a comment and are still in SAP's AV scan queue.
  // Each entry: { name: filename, addedAt: timestamp }. Polled away as they appear.
  const [pendingScanFiles, setPendingScanFiles] = useState<Array<{ name: string; addedAt: number }>>([]);
  const [alertMsg, setAlertMsg] = useState('');
  const [alertSuccess, setAlertSuccess] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { context }: ContextEntity = useContext(GlobalContext);
  const lang = localStorage.getItem('SELECTED_LNG');

  const getServiceRequestFiles = async () => {
    setAttachmentsLoading(true);
    await serviceRequestApi
      .getServiceRequestFiles(activeItem?.code)
      .then((res) => {
        const files: AttachmentEntity[] = [];
        res.forEach((file) => {
          const fileType = (file.file_name.split(".").pop() ?? "").toLowerCase();
          const mime =
            fileType === "pdf" ? "application/pdf"
            : fileType === "svg" ? "image/svg+xml"
            : fileType === "jpg" || fileType === "jpeg" ? "image/jpeg"
            : fileType === "png" ? "image/png"
            : fileType === "gif" ? "image/gif"
            : fileType === "mp4" ? "video/mp4"
            : fileType;
          files.push({
            file: new File(
              [Buffer.from(file.att_datas, "base64") as BlobPart],
              `${file.file_name}`,
              { type: mime }
            ),
            customProperties: { uploading: file?.uploading },
          });
        });
        setAttachments(files);
      })
      .catch(() => setAttachments([]));
    setAttachmentsLoading(false);
  };

  const getServiceRequest = async () => {
    setShowLoadingOverlay(true);
    await serviceRequestApi
      .getServiceRequest(activeItem?.code)
      .then((res: any) => {
        setServiceRequest(Array.isArray(res) ? (res[0] || null) : (res || null));
      })
      .catch(() => setServiceRequest(null));
    setShowLoadingOverlay(false);
  };

  useEffect(() => {
    if (activeItem?.code) {
      (async () => {
        await getServiceRequest();
        await getServiceRequestFiles();
      })();
    }
  }, [activeItem?.code]);

  // SAP queues uploads behind an AV scan for ~10–60s. If the ticket was created
  // recently and we don't yet see any attachments, poll every 5s for up to 60s
  // and surface a "scanning" hint to the user.
  const [isScanningAttachment, setIsScanningAttachment] = useState(false);
  useEffect(() => {
    if (!serviceRequest?.code) return;
    if (attachments.length > 0) { setIsScanningAttachment(false); return; }
    const createdAt = new Date(serviceRequest.create_date).getTime();
    const ageMs = Date.now() - createdAt;
    if (Number.isNaN(createdAt) || ageMs > 3 * 60 * 1000) return; // older than 3 min — skip
    setIsScanningAttachment(true);
    let attempts = 0;
    const maxAttempts = 12; // 12 × 5s = 60s
    const interval = setInterval(async () => {
      attempts += 1;
      await getServiceRequestFiles();
      if (attempts >= maxAttempts) {
        clearInterval(interval);
        setIsScanningAttachment(false);
      }
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [serviceRequest?.code, attachments.length]);

  // Poll for files added via comments. Each pending entry gets up to 60s of
  // polling; entries whose name shows up in `attachments` (or whose 60s window
  // has passed) are removed from the pending list automatically.
  useEffect(() => {
    if (pendingScanFiles.length === 0) return;
    // Drop any already-arrived names so we don't keep polling for them.
    const arrived = new Set(attachments.map((a) => (a.file?.name || (a as any).name)));
    const stillPending = pendingScanFiles.filter((p) => !arrived.has(p.name) && Date.now() - p.addedAt < 60_000);
    if (stillPending.length !== pendingScanFiles.length) {
      setPendingScanFiles(stillPending);
      return;
    }
    const interval = setInterval(() => { getServiceRequestFiles(); }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [pendingScanFiles, attachments]);

  const onFilesPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) setMessageFiles((prev) => [...prev, ...files]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (idx: number) => {
    setMessageFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const sendMessage = async () => {
    const text = messageText.trim();
    if (!text || isSending) return;
    setIsSending(true);
    try {
      await amendmentApi.createAmendment(
        { amendment: text, reportId: serviceRequest?.code ?? '' } as any,
        messageFiles,
      );
      // Track the just-sent files as "pending scan" so we can show a placeholder
      // and poll until SAP makes them available.
      if (messageFiles.length > 0) {
        const now = Date.now();
        setPendingScanFiles((prev) => [
          ...prev,
          ...messageFiles.map((f) => ({ name: f.name, addedAt: now })),
        ]);
      }
      setMessageText('');
      setMessageFiles([]);
      setAlertMsg(String(t('REPAIR_AND_INQUIRIES.SUCCESSFULLY_AMENDED')));
      setAlertSuccess(true);
      setShowAlert(true);
      await getServiceRequest();
      if (messageFiles.length > 0) await getServiceRequestFiles();
    } catch (err: any) {
      setAlertMsg(generateError(err?.response?.data?.message));
      setAlertSuccess(false);
      setShowAlert(true);
    } finally {
      setIsSending(false);
    }
  };

  const onTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      sendMessage();
    }
  };

  const currentStatusIndex = statuses.findIndex(s => s.nameAims.includes(serviceRequest?.status ?? ''));
  const currentStatus = statuses[currentStatusIndex];
  const statusKey = currentStatus?.name?.toLowerCase().replace(' ', '-') ?? 'open';
  const statusStyle = STATUS_STYLES[statusKey] ?? STATUS_STYLES['open'];
  const StatusIcon = statusStyle.Icon;
  const currentStatusLabel = lang === 'EN' ? (currentStatus?.name ?? '') : (currentStatus?.nameDe ?? '');

  const activeContract = context?.contracts?.activeContracts?.find(
    (c: ContractEntity) =>
      c.contractNumber === serviceRequest?.contract_number?.replace('/', '-') ||
      c.contractNumber === serviceRequest?.contract_number
  );

  const messageCount = serviceRequest?.user_comment?.length ?? 0;

  return (
    <div className="request-content-wrapper">
      {showLoadingOverlay && <LoadingOverlay />}

      {serviceRequest && (
        <>
          {/* Compact header — sticky so it stays visible while scrolling */}
          <div className="sticky top-0 z-20 px-6 pt-5 pb-4 bg-white border-b border-gray-200">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                style={{ fontFamily: '"Red Hat Display", sans-serif' }}
              >
                <ArrowLeftIcon className="w-4 h-4" />
                {t('HEADER.BACK')}
              </button>
              <span className="text-gray-300">|</span>
              <span
                className="text-sm font-medium"
                style={{ color: '#218cda', fontFamily: '"Red Hat Display", sans-serif' }}
              >
                {(serviceRequest as any)?.ticketnr ?? serviceRequest?.code}
              </span>
              {currentStatus && (
                <span className={`flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                  <StatusIcon className="w-3 h-3" />
                  {currentStatusLabel}
                </span>
              )}
              {serviceRequest?.main_issue && (
                <span className="px-2.5 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                  {serviceRequest.main_issue}
                </span>
              )}
              {/* Counts: messages + attachments */}
              <span
                className="flex items-center gap-1 px-2.5 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full"
                title={String(t(messageCount === 1 ? 'REPAIR_AND_INQUIRIES.MESSAGE_COUNT_ONE' : 'REPAIR_AND_INQUIRIES.MESSAGE_COUNT_OTHER', { count: messageCount }))}
              >
                <ChatBubbleLeftEllipsisIcon className="w-3 h-3" />
                {messageCount}
              </span>
              <span
                className="flex items-center gap-1 px-2.5 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full"
                title={`${attachments.length} ${t('REPAIR_AND_INQUIRIES.ATTACHMENTS')}`}
              >
                <PaperClipIcon className="w-3 h-3" />
                {attachments.length}
              </span>
            </div>
            <h2
              className="font-bold text-[#0a0d47]"
              style={{ fontFamily: '"Red Hat Display", sans-serif', fontSize: '1.125rem' }}
            >
              {serviceRequest?.subject}
            </h2>
          </div>

          <div className="px-6 py-6 max-w-3xl mx-auto">
            {/* Ticket Status card */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-4">
              <h3
                className="text-sm font-semibold mb-5"
                style={{ color: '#218cda', fontFamily: '"Red Hat Display", sans-serif' }}
              >
                {t('REPAIR_AND_INQUIRIES.TICKET_STATUS')}
              </h3>
              <div className="mb-5">
                <TicketStatusStepper
                  statuses={statuses}
                  currentStatusIndex={currentStatusIndex}
                  lang={lang}
                />
              </div>
              {currentStatus && (
                <div className="flex justify-center">
                  <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                    <StatusIcon className="w-3.5 h-3.5" />
                    {currentStatusLabel}
                  </span>
                </div>
              )}
            </div>

            {/* Request Details card */}
            <div
              className="bg-white rounded-2xl shadow-sm p-6 mb-4"
              style={{ border: '1px solid #e5e7eb', borderLeft: '4px solid #218cda' }}
            >
              <h3
                className="text-sm font-semibold mb-5"
                style={{ color: '#218cda', fontFamily: '"Red Hat Display", sans-serif' }}
              >
                {t('REPAIR_AND_INQUIRIES.REQUEST_DETAILS')}
              </h3>
              <div className="grid grid-cols-3 gap-x-6 gap-y-5 mb-5">
                {[
                  { label: t('REPAIR_AND_INQUIRIES.CONTRACT_NUMBER'), value: serviceRequest.contract_number },
                  { label: t('REPAIR_AND_INQUIRIES.ADDRESS'), value: activeContract?.address ?? '—' },
                  { label: t('REPAIR_AND_INQUIRIES.SUBMITTED'), value: moment(serviceRequest.create_date).format('DD MMM YYYY') },
                  { label: t('REPAIR_AND_INQUIRIES.CONTACTED_VIA'), value: serviceRequest.contacted_via },
                  { label: t('REPAIR_AND_INQUIRIES.REQUEST_TYPE'), value: serviceRequest.main_issue },
                  { label: t('REPAIR_AND_INQUIRIES.PRIORITY'), value: t(`REPAIR_AND_INQUIRIES.URGENCY_OPTIONS.${serviceRequest.urgency?.toUpperCase()}`) },
                  ...((): { label: string; value: string }[] => {
                    const localize = (v: string | null): string | null => {
                      if (!v) return null
                      const key = VALUE_TRANSLATION_KEYS[v]
                      return key ? t(`REPAIR_AND_INQUIRIES.FIELD_VALUES.${key}`) : v
                    }
                    const kategorie = localize(extractField(serviceRequest.description, 'Kategorie'))
                    const dringlichkeit = localize(extractField(serviceRequest.description, 'Dringlichkeit'))
                    const affectsNeighbours = localize(extractField(serviceRequest.description, 'Beeinflusst Nachbarn'))
                    return [
                      ...(kategorie ? [{ label: t('REPAIR_AND_INQUIRIES.CATEGORY'), value: kategorie }] : []),
                      ...(dringlichkeit ? [{ label: t('REPAIR_AND_INQUIRIES.URGENCY'), value: dringlichkeit }] : []),
                      ...(affectsNeighbours ? [{ label: t('REPAIR_AND_INQUIRIES.AFFECTS_NEIGHBOURS'), value: affectsNeighbours }] : []),
                    ]
                  })(),
                ].map(({ label, value }) => (
                  <div key={label}>
                    <p
                      className="text-xs font-medium text-gray-400 mb-1"
                      style={{ fontFamily: '"Red Hat Display", sans-serif', letterSpacing: '0.05em', textTransform: 'uppercase' }}
                    >
                      {label}
                    </p>
                    <p
                      className="text-sm font-medium"
                      style={{ color: '#212121', fontFamily: '"Red Hat Display", sans-serif' }}
                    >
                      {value}
                    </p>
                  </div>
                ))}
              </div>
              <div>
                <p
                  className="text-xs font-medium text-gray-400 mb-1"
                  style={{ fontFamily: '"Red Hat Display", sans-serif', letterSpacing: '0.05em', textTransform: 'uppercase' }}
                >
                  {t('REPAIR_AND_INQUIRIES.DESCRIPTION')}
                </p>
                <p
                  className="text-sm whitespace-pre-wrap"
                  style={{ color: '#212121', fontFamily: '"Red Hat Display", sans-serif' }}
                >
                  {extractUserDescription(serviceRequest.description)}
                </p>
              </div>
            </div>

            {/* Attachments card — visible while loading, scanning, OR when attachments are present */}
            {(attachmentsLoading || isScanningAttachment || pendingScanFiles.length > 0 || attachments.length > 0) && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-4">
                <h3
                  className="text-sm font-semibold mb-4"
                  style={{ color: '#218cda', fontFamily: '"Red Hat Display", sans-serif' }}
                >
                  {t('REPAIR_AND_INQUIRIES.ATTACHMENTS')}
                </h3>

                {attachments.length > 0 && (
                  <ImagePreview attachments={attachments} download={true} />
                )}

                {/* Placeholders for files pending AV scan (from comments) */}
                {pendingScanFiles.length > 0 && (
                  <div className="flex flex-wrap gap-3 mt-3" role="status" aria-live="polite">
                    {pendingScanFiles.map((p, i) => (
                      <div
                        key={`${p.name}-${p.addedAt}-${i}`}
                        className="flex items-center gap-2"
                        style={{
                          padding: '8px 12px',
                          background: '#f3f4f6',
                          border: '1px dashed #218cda',
                          borderRadius: 8,
                          fontFamily: '"Red Hat Display", sans-serif',
                        }}
                      >
                        <div
                          style={{
                            width: 16, height: 16,
                            border: '2px solid #e8f4fc',
                            borderTopColor: '#218cda',
                            borderRadius: '50%',
                            animation: 'rc-spin 0.8s linear infinite',
                          }}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span className="text-xs font-medium" style={{ color: '#212121' }}>{p.name}</span>
                          <span className="text-xs text-gray-400">{t('REPAIR_AND_INQUIRIES.SCANNING_ATTACHMENT')}</span>
                        </div>
                      </div>
                    ))}
                    <style>{`@keyframes rc-spin { to { transform: rotate(360deg); } }`}</style>
                  </div>
                )}

                {/* Initial-load scanning state (no attachments yet) */}
                {attachments.length === 0 && pendingScanFiles.length === 0 && (
                  <div
                    className="flex flex-col items-center justify-center py-6 text-center"
                    role="status"
                    aria-live="polite"
                    style={{ fontFamily: '"Red Hat Display", sans-serif' }}
                  >
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        border: '3px solid #e8f4fc',
                        borderTopColor: '#218cda',
                        borderRadius: '50%',
                        animation: 'rc-spin 0.8s linear infinite',
                        marginBottom: 12,
                      }}
                    />
                    {isScanningAttachment ? (
                      <>
                        <p className="text-sm font-semibold" style={{ color: '#212121' }}>
                          {t('REPAIR_AND_INQUIRIES.SCANNING_ATTACHMENT')}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {t('REPAIR_AND_INQUIRIES.SCANNING_HINT')}
                        </p>
                      </>
                    ) : (
                      <p className="text-xs text-gray-400">Loading…</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Feedback card — only when present */}
            {serviceRequest.zFeedbackTenantAppKTU && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-4">
                <h3
                  className="text-sm font-semibold mb-4"
                  style={{ color: '#218cda', fontFamily: '"Red Hat Display", sans-serif' }}
                >
                  {t('REPAIR_AND_INQUIRIES.FEEDBACK')}
                </h3>
                <p
                  className="text-sm"
                  style={{ color: '#212121', fontFamily: '"Red Hat Display", sans-serif' }}
                >
                  {serviceRequest.zFeedbackTenantAppKTU}
                </p>
              </div>
            )}

            {/* Messages & Comments card */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-4" style={{ fontFamily: '"Red Hat Display", sans-serif' }}>
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-sm font-semibold" style={{ color: '#218cda' }}>
                  {t('REPAIR_AND_INQUIRIES.MESSAGES_AND_COMMENTS')}
                </h3>
                <span className="text-xs text-gray-400">
                  {t(messageCount === 1 ? 'REPAIR_AND_INQUIRIES.MESSAGE_COUNT_ONE' : 'REPAIR_AND_INQUIRIES.MESSAGE_COUNT_OTHER', { count: messageCount })}
                </span>
              </div>

              {messageCount > 0 && (
                <div className="space-y-5 mb-5 pb-5 border-b border-gray-100">
                  {serviceRequest.user_comment
                    .slice()
                    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                    .map((comment: any, index: number) => {
                      const text = parseCommentText(comment?.comment);
                      const sender = t('REPAIR_AND_INQUIRIES.YOU');
                      const initials = 'ME';
                      return (
                        <div key={index} className="flex items-start gap-3">
                          <div
                            className="flex items-center justify-center rounded-full shrink-0"
                            style={{
                              width: 36,
                              height: 36,
                              background: '#e8f4fc',
                              color: '#218cda',
                              fontSize: 12,
                              fontWeight: 700,
                            }}
                          >
                            {initials}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs text-gray-500">
                                {moment.utc(new Date(comment?.date)).format('DD MMM YYYY, HH:mm')}
                              </span>
                              <span className="text-xs font-semibold" style={{ color: '#212121' }}>
                                {sender}
                              </span>
                            </div>
                            <div
                              className="inline-block max-w-full text-sm"
                              style={{
                                background: '#f3f4f6',
                                color: '#212121',
                                padding: '10px 14px',
                                borderRadius: 12,
                                lineHeight: 1.5,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                              }}
                            >
                              {text}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}

              {/* Empty state when no messages and form hidden */}
              {messageCount === 0 && !showCommentForm && (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div
                    className="flex items-center justify-center rounded-full mb-3"
                    style={{ width: 48, height: 48, background: '#f3f4f6' }}
                  >
                    <ChatBubbleLeftEllipsisIcon className="w-6 h-6 text-gray-400" />
                  </div>
                  <p className="text-sm font-semibold mb-1" style={{ color: '#212121' }}>
                    {t('REPAIR_AND_INQUIRIES.NO_MESSAGES_YET')}
                  </p>
                  <p className="text-xs text-gray-400 mb-5">
                    {t('REPAIR_AND_INQUIRIES.USE_AMEND_BUTTON')}
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowCommentForm(true)}
                    className="inline-flex items-center gap-1.5 text-sm font-medium transition-colors"
                    style={{
                      border: '1px solid #218cda',
                      color: '#218cda',
                      background: '#fff',
                      borderRadius: 8,
                      padding: '8px 16px',
                    }}
                  >
                    <PencilIcon className="w-4 h-4" />
                    {t('REPAIR_AND_INQUIRIES.AMEND')}
                  </button>
                </div>
              )}

              {/* Add comment form */}
              {(messageCount > 0 || showCommentForm) && (
              <div>
                <p className="text-xs font-medium text-gray-400 mb-2" style={{ letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  {t('REPAIR_AND_INQUIRIES.ADD_A_COMMENT')}
                </p>
                <textarea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onKeyDown={onTextareaKeyDown}
                  placeholder={String(t('REPAIR_AND_INQUIRIES.MESSAGE_PLACEHOLDER'))}
                  disabled={isSending}
                  rows={4}
                  className="w-full text-sm resize-none outline-none transition-colors"
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    padding: '12px 14px',
                    color: '#212121',
                    fontFamily: '"Red Hat Display", sans-serif',
                  }}
                />

                {messageFiles.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {messageFiles.map((f, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1.5 text-xs"
                        style={{
                          background: '#f3f4f6',
                          color: '#374151',
                          padding: '4px 8px',
                          borderRadius: 999,
                        }}
                      >
                        <PaperClipIcon className="w-3 h-3" />
                        <span className="truncate max-w-[180px]">{f.name}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(idx)}
                          aria-label={String(t('BUTTON.DELETE'))}
                          style={{ display: 'inline-flex', alignItems: 'center' }}
                        >
                          <XMarkIcon className="w-3 h-3 text-gray-500 hover:text-gray-700" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isSending}
                      className="inline-flex items-center gap-1.5 text-xs font-medium transition-colors"
                      style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 8,
                        padding: '6px 12px',
                        background: '#fff',
                        color: '#374151',
                      }}
                    >
                      <PaperClipIcon className="w-4 h-4" />
                      {t('REPAIR_AND_INQUIRIES.ATTACH')}
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={onFilesPicked}
                      style={{ display: 'none' }}
                    />
                    <span className="text-xs text-gray-400">{t('REPAIR_AND_INQUIRIES.CTRL_ENTER_TO_SEND')}</span>
                  </div>
                  <button
                    type="button"
                    onClick={sendMessage}
                    disabled={!messageText.trim() || isSending}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-white transition-colors"
                    style={{
                      background: messageText.trim() && !isSending ? '#218cda' : '#9ec8ec',
                      borderRadius: 8,
                      padding: '8px 16px',
                      cursor: messageText.trim() && !isSending ? 'pointer' : 'not-allowed',
                    }}
                  >
                    <PaperAirplaneIcon className="w-4 h-4" />
                    {isSending ? t('BUTTON.PROCESSING') : t('REPAIR_AND_INQUIRIES.SEND')}
                  </button>
                </div>
              </div>
              )}
            </div>
          </div>
        </>
      )}
      <Snackbar
        open={showAlert}
        autoHideDuration={6000}
        onClose={() => setShowAlert(false)}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Alert
          severity={alertSuccess ? 'success' : 'error'}
          sx={{ width: '100%' }}
          variant="filled"
          role={alertSuccess ? 'status' : 'alert'}
          aria-live={alertSuccess ? 'polite' : 'assertive'}
        >
          {alertMsg}
        </Alert>
      </Snackbar>
    </div>
  );
};
